import {Component, ViewEncapsulation} from '@angular/core';
import {TableModule} from "primeng/table";
import {ButtonDirective} from "primeng/button";
import {DatePipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {TooltipModule} from "primeng/tooltip";
import {DialogModule} from "primeng/dialog";
import {TabViewModule} from "primeng/tabview";
import {AutoCompleteModule} from "primeng/autocomplete";
import {FormsModule} from "@angular/forms";
import {ChipModule} from "primeng/chip";
import { DropdownModule } from 'primeng/dropdown';
import {InputTextModule} from "primeng/inputtext";
import {NgxExtendedPdfViewerModule} from "ngx-extended-pdf-viewer";
import {InputTextareaModule} from "primeng/inputtextarea";
import { MessagesService } from '@core/_services/messages.service';
import { Communication } from '@service/communication';
import { lastValueFrom } from 'rxjs';
import { SharedDocumalisModule } from '@documalis/shared-documalis.module';
import { DocumentService } from '@service/document.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditorService } from '@service/editor.service';
import { ExecRegExp } from '@core/_utils/tools.utils';
import { EmailCtrl } from '../_const/const';


@Component({
  selector: 'dock-signengine',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './docksignengine.component.html',
  imports: [
    TableModule,
    ButtonDirective,
    NgClass,
    DatePipe,
    TooltipModule,
    DialogModule,
    TabViewModule,
    AutoCompleteModule,
    FormsModule,
    ChipModule,
    InputTextModule,
    NgxExtendedPdfViewerModule,
    NgIf,
    InputTextareaModule,
    DropdownModule,
    NgForOf,
    SharedDocumalisModule,
    TranslateModule
  ],
  styleUrls: ['./docksignengine.component.scss']
})

export class DockSignEngineComponent {
  public displayModal = false;
  public messageSubject = '';
  public messageBody = '';

  public recipient: any; // Object instead of string to store selected recipient
  public filteredRecipients: any[] = []; // Array to hold the filtered recipient objects
  public selectedRecipients: any[] = []; // Array to hold selected recipient objects

  public pdfSrc : string = '';
  public options = [];
  public users = [];
  public signType: string = 'SIGNER';
  public docId: string;
  public bskMode: boolean = false;

  // Champs pour le destinataire externe
  public externalFirstLastName = '';
  public externalEmail = '';

  // Liste d'exemples de destinataires avec Nom, Prénom et Email
  public allRecipients: any[];

  constructor(private ms: MessagesService, private communication: Communication, private documentService: DocumentService,
    protected translate: TranslateService, private editorService: EditorService
  ) {
    this.users = [];
    this.allRecipients = [];
    this.options = [];
    this.docId = '';
    this.bskMode = false;
  }

  ngOnInit() {
    this.getEmailList();
    this.setOptions();
    this.docId = this.documentService.getDocId();
    this.bskMode = this.documentService.getBskMode();
    this.getSignEngineInfo();
  }

  private getSignEngineInfo() {
    let wVal: string = '';
    let wParser = new DOMParser();
    let wxmlDoc: any;    
    wVal = this.documentService.getPropertyValue('sys_signengine');
    if (wVal !== '') {
      wxmlDoc = wParser.parseFromString(wVal, 'text/xml');
      this.users = this.getRecipientList(wxmlDoc);
    }
  }

  private getRecipientList(aXmlDoc) {
    let wNodes: any;
    let wRecipient: any;
    let wInfosMail: any;
    let wDestArray: any;
    let wUpdateDate = '';
    let maxi = 0;
    let wStatus = {label: '', color: ''};
    wDestArray = new Array();
    wNodes = aXmlDoc.evaluate(
        '/DocumentServer/Recipient',
        aXmlDoc,
        null,
        XPathResult.UNORDERED_NODE_ITERATOR_TYPE,
        null
    );
    wRecipient = wNodes.iterateNext();
    if (wRecipient !== null) {
      const recipientElements = aXmlDoc.getElementsByTagName('recip');
      maxi = recipientElements.length;
      for (let i = 0; i < maxi; i++) {
        wInfosMail = new Object();
        const recipientElement = recipientElements[i];
        wInfosMail.documentId = this.getRecipientValue(recipientElement, 'documentId');
        wInfosMail.id = this.getRecipientValue(recipientElement, 'id');
        wInfosMail.name = this.getRecipientValue(recipientElement, 'name');
        wInfosMail.email = this.getRecipientValue(recipientElement, 'email');
        wInfosMail.status = this.getRecipientValue(recipientElement, 'signingStatus');
        wStatus = this.getStatusLabel(wInfosMail.status);
        wInfosMail.statusLabel = wStatus.label;
        wInfosMail.statusColor = wStatus.color;
        wUpdateDate = this.getRecipientValue(recipientElement, 'signedAt');
        if (wUpdateDate !== '') {
          wInfosMail.updateDate = new Date(wUpdateDate);
        }
        wDestArray.push(wInfosMail);
      }
    }
    return wDestArray;
  }

  private getRecipientValue(recipientElement, aName) {
    if (recipientElement.getElementsByTagName(aName)[0].textContent) {
      return recipientElement.getElementsByTagName(aName)[0].textContent;
    } else {
      return ''; 
    }
  }

  private getStatusLabel(aStatus) {
    switch (aStatus) {
      case 'SIGNED':
        return {label: this.translate.instant('Sign_StateSign'), color: 'bg-green-100 text-green-700'};
      case 'NOT_SIGNED':
        return {label: this.translate.instant('Sign_StateNotSign'), color: 'bg-red-100 text-red-700'};
      default:
        return {label: '', color: 'bg-gray-100 text-gray-700'};
    }   
  }

  // Filtrer les destinataires
  public filterRecipients(event: any): void {
    const query = event.query.toLowerCase();
    this.filteredRecipients = this.allRecipients
        .filter(recipient =>
            (recipient.fullName.toLowerCase().includes(query) || recipient.email.toLowerCase().includes(query)) &&
            !this.selectedRecipients.includes(recipient) // Exclure les destinataires déjà sélectionnés
        );
  }

  // Ajouter un destinataire (interne ou externe)
  public addRecipient(isExternal: boolean = false): void {
    if (isExternal) {
      this.setExternalRecipient();
    } else {
      this.setInternalRecipient();
    }
  }

  private setExternalRecipient() {
    if (this.verifyRecipient() === false) {
      return;
    }
    if (this.verifySend() === false) {
      return;
    }
    const newRecipient = {
      email: this.externalEmail,
      fullName: this.externalFirstLastName,
      option: this.signType
    };
    if (!this.selectedRecipients.some(r => r.email === newRecipient.email)) {
      this.selectedRecipients.push(newRecipient);
    }
    this.externalFirstLastName = '';
    this.externalEmail = '';
  }

  private setInternalRecipient() {
    if (this.filteredRecipients.length === 0) {
      return;       
    }
    if (this.recipient && !this.selectedRecipients.includes(this.recipient)) {
      this.recipient.option = this.signType
      this.selectedRecipients.push(this.recipient);
      this.recipient = null; // Réinitialiser le champ de saisie après l'ajout
    }
  }

  private verifyRecipient(): boolean {
    if (this.externalEmail.trim() === '' || this.externalFirstLastName.trim() === '') {
      return false;
    }
    if (!ExecRegExp(EmailCtrl, '', this.externalEmail.trim())) {
      this.ms.addErrorNoStick(this.translate.instant('Cf_WrongMail'));
      return false;
    }
    return true;
  }

  private verifySend(): boolean {
    if (this.messageSubject.trim() === '' && this.messageBody.trim() === '') {
      this.ms.addErrorNoStick(this.translate.instant('Sign_NoObj'));
      return false;
    }
    return true;
  }

  // Supprimer un destinataire
  public removeRecipient(recipient: any): void {
    this.selectedRecipients = this.selectedRecipients.filter(r => r !== recipient);
  }

  public openModal(): void {
    this.displayModal = true;
    this.pdfSrc = localStorage.getItem('urlsource') + '#view=FitV';
    console.log('openModal pdfSrc', this.pdfSrc);
  }

  public closeModal(): void {
    this.displayModal = false;
  }

  public sendMessage() {
    if (this.selectedRecipients.length === 0) {
      return;
    }
    this.execSendMessage()
  }

  private async execSendMessage() {
    let wData: any;
    let wDestArray = [];
    this.selectedRecipients.forEach(item => {
      wDestArray.push({name: item.fullName, email: item.email, role: item.option});
    })
    wData = {
      docid: this.docId,
      recipients: wDestArray,
      title: this.messageSubject,
      subject: this.messageBody
    }
    try {
      try {
        await lastValueFrom(this.communication.postUrl('/signengine/document', wData)).then(
          r => {
            this.ms.addSuccess(this.translate.instant('Sign_DocSend'));
          }
        ); 
      } catch (error) {
          console.log('Error', error);
          this.ms.addErrorMsg(this.translate.instant('Send_KO'));
          return;
      }
    } finally {
      this.displayModal = false;
    }
  }

  public async resendSignature(aUser: any) {
    let wData: any;
    let wDestArray = [aUser.id];
    wData = {
      docid: aUser.documentId,
      recipients: wDestArray,
    }
    try {
      await lastValueFrom(this.communication.postUrl('/signengine/document/' + aUser.documentId + '/resend', wData)).then(
        r => {
          this.ms.addSuccess(this.translate.instant('Sign_DocSend'));
        }
      ); 
    } catch (error) {
        console.log('Error', error);
        this.ms.addErrorMsg(this.translate.instant('Send_KO'));
        return;
    }
  }

  private getEmailList() {
    // Liste d'exemples de destinataires avec Nom, Prénom et Email
    let wRes: any;
    this.allRecipients = [];
    wRes = this.editorService.getOptionComboValueData('signengine');
    if (wRes.data !== undefined) {
      for (let i = 0; i < wRes.data.show.length; i++) {
          if (wRes.data.show[i] !== '' && (ExecRegExp(EmailCtrl, '', wRes.data.value[i]))) {
              this.allRecipients.push({fullName: wRes.data.show[i], email: wRes.data.value[i]});
          }
      }      
    }
  }

  private setOptions() {  
    this.options = [
      {label: this.translate.instant('Sign_Signer'), value: 'SIGNER'},
      {label: this.translate.instant('Sign_Approver'), value: 'APPROVER'},
      {label: this.translate.instant('Sign_Viewer'), value: 'VIEWER'},
      {label: this.translate.instant('Sign_CC'), value: 'CC'}
    ];
  }
}
